import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ShoppingCart, FilePdf, WhatsappLogo, Trash, Plus, Minus,
  ListChecks, Warehouse, CheckCircle, MagnifyingGlass,
  ClockCounterClockwise, Eye, Package, CalendarBlank, FileXls, Gear, Wrench,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadBase64File } from "@/utils/downloadHelper";
import { useLanguage } from "@/contexts/LanguageContext";
import ShoppingExportModals from "@/components/shopping/ShoppingExportModals";
import SavedListViewDialog from "@/components/shopping/SavedListViewDialog";
import ShoppingListResult from "@/components/shopping/ShoppingListResult";
import { jsPDF } from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

export default function ShoppingList() {
  const location = useLocation();
  const { t } = useLanguage();
  const [sheets, setSheets] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);
  const [showExportSelector, setShowExportSelector] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [runningDiag, setRunningDiag] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [extraIngredients, setExtraIngredients] = useState([]);
  const [shoppingList, setShoppingList] = useState(null);
  const [stockDeducted, setStockDeducted] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [purchaseConfirmed, setPurchaseConfirmed] = useState(false);
  const [eventName, setEventName] = useState(null);
  const [sheetSearch, setSheetSearch] = useState("");
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [activeTab, setActiveTab] = useState("nova");
  const [savedLists, setSavedLists] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [viewingList, setViewingList] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [listName, setListName] = useState("");

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch único no mount (axios/API são estáveis)

  useEffect(() => {
    if (location.state?.fromEvent && sheets.length > 0) {
      const event = location.state.fromEvent;
      setEventName(event.name);
      setSelectedSheets(event.sheets.map((s) => ({ sheet_id: s.sheet_id, quantity: s.quantity })));
      window.history.replaceState({}, document.title);
    }
  }, [location.state, sheets]);

  const fetchData = async () => {
    try {
      const [sheetsRes, ingredientsRes] = await Promise.all([
        axios.get(`${API}/technical-sheets`),
        axios.get(`${API}/ingredients`),
      ]);
      setSheets(sheetsRes.data);
      setIngredients(ingredientsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedLists = async () => {
    setLoadingSaved(true);
    try {
      const res = await axios.get(`${API}/shopping-lists`);
      setSavedLists(res.data);
    } catch (error) {
      toast.error("Erro ao carregar listas salvas");
    } finally {
      setLoadingSaved(false);
    }
  };

  useEffect(() => {
    if (activeTab === "salvas") fetchSavedLists();
  }, [activeTab]);

  // Sheet selection
  const toggleSheet = (sheetId) => {
    setSelectedSheets((prev) => {
      const exists = prev.find((s) => s.sheet_id === sheetId);
      if (exists) return prev.filter((s) => s.sheet_id !== sheetId);
      return [...prev, { sheet_id: sheetId, quantity: 1 }];
    });
  };

  const updateQuantity = (sheetId, delta) => {
    setSelectedSheets((prev) =>
      prev.map((s) => s.sheet_id === sheetId ? { ...s, quantity: Math.max(1, s.quantity + delta) } : s)
    );
  };

  const setQuantityDirect = (sheetId, value) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) return;
    setSelectedSheets((prev) =>
      prev.map((s) => s.sheet_id === sheetId ? { ...s, quantity: parsed } : s)
    );
  };

  // Extra ingredients
  const toggleExtraIngredient = (ingId) => {
    setExtraIngredients((prev) => {
      const exists = prev.find((e) => e.ingredient_id === ingId);
      if (exists) return prev.filter((e) => e.ingredient_id !== ingId);
      const ing = ingredients.find((i) => i.id === ingId);
      return [...prev, { ingredient_id: ingId, quantity: ing?.quantity_purchased || 1 }];
    });
  };

  const updateExtraQty = (ingId, value) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed <= 0) return;
    setExtraIngredients((prev) =>
      prev.map((e) => e.ingredient_id === ingId ? { ...e, quantity: parsed } : e)
    );
  };

  const isExtraSelected = (ingId) => extraIngredients.some((e) => e.ingredient_id === ingId);
  const getExtraQty = (ingId) => extraIngredients.find((e) => e.ingredient_id === ingId)?.quantity || 0;

  const getSelectedQuantity = (sheetId) => selectedSheets.find((s) => s.sheet_id === sheetId)?.quantity || 0;
  const isSelected = (sheetId) => selectedSheets.some((s) => s.sheet_id === sheetId);

  const canGenerate = selectedSheets.length > 0 || extraIngredients.length > 0;

  const handleGenerate = async () => {
    if (!canGenerate) { toast.error("Selecione pelo menos uma ficha ou ingrediente"); return; }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/shopping-list/generate`, {
        items: selectedSheets,
        extra_ingredients: extraIngredients,
      });
      setShoppingList(response.data);
      setStockDeducted(false);
      setPurchaseConfirmed(false);
      toast.success("Lista de compras gerada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar lista de compras");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateWithStock = async () => {
    if (!canGenerate) { toast.error("Selecione pelo menos uma ficha ou ingrediente"); return; }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/shopping-list/generate-with-stock`, {
        items: selectedSheets,
        extra_ingredients: extraIngredients,
      });
      setShoppingList(response.data);
      setStockDeducted(true);
      setPurchaseConfirmed(false);
      toast.success("Lista gerada com desconto do estoque!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar lista de compras");
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!shoppingList || !shoppingList.items) return;
    const itemsToAdd = shoppingList.items
      .filter((item) => item.packages_needed > 0)
      .map((item) => ({
        ingredient_id: item.ingredient_id,
        quantity: item.packages_needed * item.package_size,
        unit: item.package_unit || item.unit,
      }));
    if (itemsToAdd.length === 0) { toast.error("Nenhum item para confirmar"); return; }
    if (!window.confirm(`Confirmar compra de ${itemsToAdd.length} ingrediente(s)?\nTudo será adicionado ao estoque.`)) return;
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/stock/confirm-purchase`, { items: itemsToAdd });
      if (res.data.success) {
        toast.success(res.data.message);
        setPurchaseConfirmed(true);
      }
    } catch (error) {
      toast.error("Erro ao confirmar compra");
    } finally {
      setConfirming(false);
    }
  };

  const handleSaveList = async () => {
    if (!shoppingList) return;
    setSavingList(true);
    try {
      const name = listName.trim() || `Lista ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
      await axios.post(`${API}/shopping-lists`, {
        name,
        items: shoppingList.items,
        total_cost: shoppingList.total_cost,
        sheets_used: shoppingList.sheets_used || [],
        extra_ingredients_used: shoppingList.extra_ingredients_used || [],
        stock_deducted: stockDeducted,
      });
      toast.success("Lista salva com sucesso!");
      setListName("");
    } catch (error) {
      toast.error("Erro ao salvar lista");
    } finally {
      setSavingList(false);
    }
  };

  const handleViewSavedList = (list) => {
    setViewingList(list);
    setViewDialogOpen(true);
  };

  const handleDeleteSavedList = async (listId) => {
    if (!window.confirm("Deletar esta lista?")) return;
    try {
      await axios.delete(`${API}/shopping-lists/${listId}`);
      toast.success("Lista deletada!");
      setSavedLists((prev) => prev.filter((l) => l.id !== listId));
    } catch (error) {
      toast.error("Erro ao deletar lista");
    }
  };

  const handleConfirmSavedList = async (listId) => {
    if (!window.confirm("Confirmar compra desta lista? Os itens serão adicionados ao estoque.")) return;
    try {
      const res = await axios.put(`${API}/shopping-lists/${listId}/confirm`);
      if (res.data.success) {
        toast.success(res.data.message);
        setSavedLists((prev) =>
          prev.map((l) => l.id === listId ? { ...l, purchase_confirmed: true } : l)
        );
        if (viewingList?.id === listId) {
          setViewingList((prev) => ({ ...prev, purchase_confirmed: true }));
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao confirmar compra");
    }
  };

  // PDF generation for any list data
  const generatePDFFromData = (listData, sheetsUsedText) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text("Lista de Compras", 20, 20);

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    if (listData.name) {
      doc.text(listData.name, 20, 28);
    }
    const dateText = listData.created_at
      ? new Date(listData.created_at).toLocaleDateString("pt-BR")
      : new Date().toLocaleDateString("pt-BR");
    doc.text(`Gerada em: ${dateText}`, 20, listData.name ? 35 : 28);

    doc.setLineWidth(0.5);
    const startY = listData.name ? 40 : 33;
    doc.line(20, startY, 190, startY);

    let yPos = startY + 9;
    if (sheetsUsedText && sheetsUsedText.length > 0) {
      doc.setFont(undefined, "bold");
      doc.text("FICHAS TÉCNICAS:", 20, yPos);
      yPos += 7;
      doc.setFont(undefined, "normal");
      sheetsUsedText.forEach((t) => {
        doc.text(`• ${t}`, 25, yPos);
        yPos += 6;
      });
      yPos += 5;
      doc.line(20, yPos, 190, yPos);
      yPos += 8;
    }

    // Table header
    doc.setFont(undefined, "bold");
    doc.setFontSize(8);
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 5, 170, 8, "F");
    doc.text("Ingrediente", 22, yPos);
    doc.text("Qtd. Necessaria", 75, yPos);
    doc.text("Pacotes", 110, yPos);
    doc.text("Tam. Pacote", 133, yPos);
    doc.text("Custo Total", 175, yPos, { align: "right" });

    yPos += 8;
    doc.setFont(undefined, "normal");

    const items = listData.items || [];
    items.forEach((item, index) => {
      if (yPos > 270) { doc.addPage(); yPos = 20; }
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(20, yPos - 5, 170, 7, "F");
      }
      doc.text(item.ingredient_name || "", 22, yPos);
      doc.text(`${item.total_quantity_needed} ${item.unit || ""}`, 75, yPos);
      doc.text(`${item.packages_needed}`, 110, yPos);
      doc.text(`${item.package_size} ${item.package_unit || item.unit || ""}`, 133, yPos);
      doc.text(formatCurrency(item.total_cost), 175, yPos, { align: "right" });
      yPos += 7;
    });

    yPos += 3;
    doc.setLineWidth(0.5);
    doc.line(20, yPos, 190, yPos);
    yPos += 7;
    doc.setFont(undefined, "bold");
    doc.setFontSize(11);
    doc.text("TOTAL:", 130, yPos);
    doc.text(formatCurrency(listData.total_cost || 0), 175, yPos, { align: "right" });

    const pageCount = doc.internal.pages.length - 1;
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Gerado por SOP & RAFT - ${new Date().toLocaleDateString("pt-BR")}`, 105, 290, { align: "center" });
      doc.text(`Pagina ${i} de ${pageCount}`, 190, 290, { align: "right" });
    }
    return doc;
  };

  const generatePDF = () => {
    if (!shoppingList) return;
    const sheetsText = selectedSheets.map((sel) => {
      const sheet = sheets.find((s) => s.id === sel.sheet_id);
      return sheet ? `${sheet.name} (x${sel.quantity})` : "";
    }).filter(Boolean);
    const doc = generatePDFFromData(shoppingList, sheetsText);
    doc.save(`lista-compras-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF gerado com sucesso!");
  };

  const generateSavedListPDF = (list) => {
    const sheetsText = (list.sheets_used || []).map((s) => `${s.sheet_name} (x${s.quantity})`);
    const doc = generatePDFFromData(list, sheetsText);
    doc.save(`lista-${(list.name || "compras").toLowerCase().replace(/\s+/g, "-")}.pdf`);
    toast.success("PDF gerado!");
  };

  const handleShareWhatsApp = () => {
    if (!shoppingList) return;
    const sheetsText = selectedSheets.map((sel) => {
      const sheet = sheets.find((s) => s.id === sel.sheet_id);
      return sheet ? `  • ${sheet.name} (x${sel.quantity})` : "";
    }).filter(Boolean).join("\n");
    const extraText = extraIngredients.map((ei) => {
      const ing = ingredients.find((i) => i.id === ei.ingredient_id);
      return ing ? `  • ${ing.name} (${ei.quantity} ${ing.unit})` : "";
    }).filter(Boolean).join("\n");
    const itemsText = shoppingList.items.map(
      (item) => `• ${item.ingredient_name}: ${item.total_quantity_needed} ${item.unit} → ${item.packages_needed} pacote(s) = ${formatCurrency(item.total_cost)}`
    ).join("\n");
    let text = `🛒 *Lista de Compras*\n\n`;
    if (sheetsText) text += `*Fichas selecionadas:*\n${sheetsText}\n\n`;
    if (extraText) text += `*Ingredientes avulsos:*\n${extraText}\n\n`;
    text += `*Itens:*\n${itemsText}\n\n*TOTAL: ${formatCurrency(shoppingList.total_cost)}*\n\n_Gerado por SOP & RAFT_`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("Abrindo WhatsApp...");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="shopping-list-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
      </div>
    );
  }

  const handleRunDiag = async () => {
    setRunningDiag(true);
    setShowDiag(true);
    setDiagResult(null);
    try {
      const { data } = await axios.get(`${API}/diag/shopping`);
      setDiagResult(data);
    } catch (err) {
      setDiagResult({
        all_ok: false,
        steps: [{ step: "Chamada do diagnóstico", ok: false, error: err.response?.data?.detail || err.message }],
      });
    }
    setRunningDiag(false);
  };

  const handleExportShoppingExcel = async () => {
    if (selectedSheets.length === 0 && extraIngredients.length === 0) {
      toast.error("Selecione fichas ou ingredientes avulsos para exportar");
      return;
    }
    setExportingExcel(true);
    try {
      const { data } = await axios.post(`${API}/export/shopping-from-sheets`, {
        sheets: selectedSheets,
        extra_ingredients: extraIngredients,
      });
      downloadBase64File(
        data.file_base64,
        data.file_name,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      toast.success(`Lista exportada com ${data.items_count} ingredientes (R$ ${data.grand_total})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao exportar");
    }
    setExportingExcel(false);
  };

  return (
    <div className="space-y-6" data-testid="shopping-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950" data-testid="shopping-list-title">
            {t("sl_title")}
          </h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">
            {eventName ? `${eventName}` : t("sl_subtitle")}
          </p>
        </div>
        <div className="flex gap-1 w-fit">
          <Button
            onClick={() => setShowBuilder(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="open-shopping-builder-btn"
          >
            <FileXls size={18} className="mr-1.5" />
            Gerar Lista pelo Modelo
          </Button>
          <Button
            onClick={handleExportShoppingExcel}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="export-shopping-btn"
            disabled={exportingExcel || (selectedSheets.length === 0 && extraIngredients.length === 0)}
            title="Exporta apenas o que está selecionado abaixo"
          >
            <FileXls size={18} className="mr-1.5" />
            {exportingExcel ? "Exportando..." : "Exportar Selecionados"}
          </Button>
          <Button
            onClick={() => setShowExportSelector(true)}
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-zinc-400 hover:text-emerald-600"
            title="Exportar ingredientes avulsos"
            data-testid="export-ingredients-btn"
          >
            <Package size={18} />
          </Button>
          <Button
            onClick={handleRunDiag}
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-zinc-400 hover:text-amber-600"
            title="Diagnóstico da lista de compras"
            data-testid="run-shopping-diag-btn"
          >
            <Wrench size={18} />
          </Button>
          <Button
            onClick={() => setShowTemplateConfig(true)}
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-zinc-400 hover:text-emerald-600"
            title="Configurar modelo de exportacao"
            data-testid="config-template-shopping-btn"
          >
            <Gear size={18} />
          </Button>
        </div>
        <ShoppingExportModals {...{ activeTab, diagResult, runningDiag, setActiveTab, setShowBuilder, setShowDiag, setShowExportSelector, setShowTemplateConfig, showBuilder, showDiag, showExportSelector, showTemplateConfig, t }} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="nova" data-testid="tab-nova-lista">
            <ShoppingCart size={16} className="mr-2" /> {t("sl_new_list")}
          </TabsTrigger>
          <TabsTrigger value="salvas" data-testid="tab-listas-salvas">
            <ClockCounterClockwise size={16} className="mr-2" /> {t("sl_saved_lists")}
          </TabsTrigger>
        </TabsList>

        {/* ========== TAB: Nova Lista ========== */}
        <TabsContent value="nova" className="mt-4 space-y-4">
          {/* Sheet Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ListChecks size={20} />
                Fichas Técnicas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sheets.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6" data-testid="no-sheets-message">
                  Nenhuma ficha técnica cadastrada.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      data-testid="shopping-sheet-search"
                      value={sheetSearch}
                      onChange={(e) => setSheetSearch(e.target.value)}
                      placeholder="Buscar ficha técnica..."
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {sheets.filter((s) => s.name.toLowerCase().includes(sheetSearch.toLowerCase())).map((sheet) => {
                      const selected = isSelected(sheet.id);
                      return (
                        <div
                          key={sheet.id}
                          data-testid={`sheet-select-${sheet.id}`}
                          className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${
                            selected ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => toggleSheet(sheet.id)}
                              data-testid={`sheet-checkbox-${sheet.id}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-zinc-900">{sheet.name}</p>
                              <p className="text-xs text-zinc-500">
                                {sheet.yield_quantity} {sheet.yield_unit} · Custo: {formatCurrency(sheet.total_cost)} · {sheet.ingredients.length} ingrediente(s)
                              </p>
                            </div>
                          </div>
                          {selected && (
                            <div className="flex items-center gap-1.5">
                              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(sheet.id, -1)}>
                                <Minus size={14} />
                              </Button>
                              <Input
                                type="number" min="1" value={getSelectedQuantity(sheet.id)}
                                onChange={(e) => setQuantityDirect(sheet.id, e.target.value)}
                                className="w-14 h-7 text-center text-sm"
                              />
                              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(sheet.id, 1)}>
                                <Plus size={14} />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Extra Ingredients */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package size={20} />
                Ingredientes Avulsos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ingredients.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">Nenhum ingrediente cadastrado.</p>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      data-testid="extra-ingredient-search"
                      value={ingredientSearch}
                      onChange={(e) => setIngredientSearch(e.target.value)}
                      placeholder="Buscar ingrediente avulso..."
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {ingredients
                      .filter((i) => i.name.toLowerCase().includes(ingredientSearch.toLowerCase()))
                      .map((ing) => {
                        const selected = isExtraSelected(ing.id);
                        return (
                          <div
                            key={ing.id}
                            data-testid={`extra-ing-${ing.id}`}
                            className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${
                              selected ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={selected}
                                onCheckedChange={() => toggleExtraIngredient(ing.id)}
                                data-testid={`extra-ing-check-${ing.id}`}
                              />
                              <div>
                                <p className="text-sm font-medium text-zinc-900">{ing.name}</p>
                                <p className="text-xs text-zinc-500">
                                  {ing.quantity_purchased} {ing.unit} · {formatCurrency(ing.purchase_price)}
                                  {ing.category && <span className="ml-1 text-zinc-400">· {ing.category}</span>}
                                </p>
                              </div>
                            </div>
                            {selected && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-zinc-500">{ing.unit}:</span>
                                <Input
                                  type="number" step="0.01" min="0.01"
                                  value={getExtraQty(ing.id)}
                                  onChange={(e) => updateExtraQty(ing.id, e.target.value)}
                                  className="w-20 h-7 text-center text-sm"
                                  data-testid={`extra-ing-qty-${ing.id}`}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generate buttons */}
          {(sheets.length > 0 || ingredients.length > 0) && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-zinc-500">
                {selectedSheets.length} ficha(s) + {extraIngredients.length} avulso(s) selecionado(s)
              </p>
              <div className="flex gap-2">
                <Button onClick={handleGenerate} disabled={!canGenerate || generating} data-testid="generate-shopping-list-btn">
                  <ShoppingCart size={16} className="mr-2" />
                  {generating ? "Gerando..." : "Gerar Lista"}
                </Button>
                <Button
                  onClick={handleGenerateWithStock}
                  disabled={!canGenerate || generating}
                  variant="outline"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                  data-testid="generate-with-stock-btn"
                >
                  <Warehouse size={16} className="mr-2" />
                  {generating ? "Gerando..." : "Descontar Estoque"}
                </Button>
              </div>
            </div>
          )}

          {/* Generated List Result */}
          {shoppingList && (
            <ShoppingListResult
              shoppingList={shoppingList}
              stockDeducted={stockDeducted}
              purchaseConfirmed={purchaseConfirmed}
              confirming={confirming}
              onConfirmPurchase={handleConfirmPurchase}
              onGeneratePDF={generatePDF}
              onShareWhatsApp={handleShareWhatsApp}
              onSaveList={handleSaveList}
              savingList={savingList}
              listName={listName}
              onListNameChange={setListName}
            />
          )}
        </TabsContent>

        {/* ========== TAB: Listas Salvas ========== */}
        <TabsContent value="salvas" className="mt-4">
          {loadingSaved ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
            </div>
          ) : savedLists.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ClockCounterClockwise size={48} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-500" data-testid="no-saved-lists">Nenhuma lista salva.</p>
                <p className="text-sm text-zinc-400 mt-1">Gere e salve uma lista na aba &ldquo;Nova Lista&rdquo;</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {savedLists.map((list) => (
                <Card key={list.id} className="border-zinc-200 shadow-sm" data-testid={`saved-list-${list.id}`}>
                  <CardContent className="py-4 px-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{list.name}</p>
                          {list.purchase_confirmed ? (
                            <span className="text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded shrink-0">
                              Confirmada
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded shrink-0">
                              Pendente
                            </span>
                          )}
                          {list.stock_deducted && (
                            <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded shrink-0">
                              Estoque descontado
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <CalendarBlank size={12} />
                            {new Date(list.created_at).toLocaleDateString("pt-BR")}
                          </span>
                          <span>{(list.items || []).length} itens</span>
                          <span className="font-mono font-semibold text-zinc-700">{formatCurrency(list.total_cost)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 ml-3">
                        <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => handleViewSavedList(list)} data-testid={`view-saved-${list.id}`}>
                          <Eye size={14} className="mr-1" /> Ver
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => generateSavedListPDF(list)} data-testid={`pdf-saved-${list.id}`}>
                          <FilePdf size={14} className="mr-1" /> PDF
                        </Button>
                        {!list.purchase_confirmed && (
                          <Button
                            size="sm" className="text-xs h-8 bg-zinc-900 text-white hover:bg-zinc-800"
                            onClick={() => handleConfirmSavedList(list.id)}
                            data-testid={`confirm-saved-${list.id}`}
                          >
                            <CheckCircle size={14} className="mr-1" /> Confirmar
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm"
                          className="text-xs h-8 text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={() => handleDeleteSavedList(list.id)}
                          data-testid={`delete-saved-${list.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <SavedListViewDialog {...{ formatCurrency, generateSavedListPDF, handleConfirmSavedList, setViewDialogOpen, t, viewDialogOpen, viewingList }} />
    </div>
  );
}
